/**
 * Типы и модели каталога.
 *
 * Каталог показывает не каждую модель, а тип предмета: одна карточка «Кровать
 * двуспальная», а не серая, тауп и дубовая отдельно. Модель выбирают у поставленного
 * предмета — среди вариантов его типа. Элемент без `family` — сам себе тип.
 */
import { sectionOfItem } from "./catalog";
import type { CatalogRef } from "@/shared/plan";
import type { CatalogItem, CatalogItemBase, CatalogSection } from "./types";

/** Карточка каталога: элемент, который с неё ставится, и её название. */
export interface CatalogCard<Item extends CatalogItemBase> {
	item: Item;
	title: string;
	/** Сколько моделей у типа — больше одной, значит у поставленного будут варианты */
	variants: number;
}

/**
 * Карточки раздела: по одной на тип, на месте первой его модели в списке. `items` —
 * уже отобранные элементы (подгруппа, поиск); модели типа, не попавшие в отбор, не считаются.
 */
export const catalogCards = <Item extends CatalogItemBase>(
	section: CatalogSection<Item>,
	items: readonly Item[] = section.items,
): CatalogCard<Item>[] => {
	const cards: CatalogCard<Item>[] = [];
	const byFamily = new Map<string, CatalogCard<Item>>();
	for (const item of items) {
		const family = item.family ? section.families?.find((f) => f.id === item.family) : undefined;
		if (!family) {
			cards.push({ item, title: item.name, variants: 1 });
			continue;
		}
		const card = byFamily.get(family.id);
		if (card) {
			card.variants += 1;
			if (item.id === family.defaultItem) card.item = item;
			continue;
		}
		const created = { item, title: family.name, variants: 1 };
		byFamily.set(family.id, created);
		cards.push(created);
	}
	return cards;
};

/** Модели того же типа, что элемент по ссылке, включая его самого; пусто — вариантов нет. */
export const familyVariants = (ref: CatalogRef): readonly CatalogItem[] => {
	const section = sectionOfItem(ref);
	const item = section?.items.find((candidate) => candidate.id === ref.item);
	if (!section || !item?.family) return [];
	const variants = section.items.filter((candidate) => candidate.family === item.family);
	return variants.length > 1 ? variants : [];
};

/** Название типа элемента по ссылке; нет типа — его собственное название. */
export const familyName = (ref: CatalogRef): string | null => {
	const section = sectionOfItem(ref);
	const item = section?.items.find((candidate) => candidate.id === ref.item);
	if (!section || !item) return null;
	return section.families?.find((family) => family.id === item.family)?.name ?? item.name;
};
